"use client"

import { VideoType } from "@/app/types/video"
import { Box } from "@mui/material"
import { useAppSelector } from "@v2/lib/redux/hooks"
import { useState } from "react"
import CustomHlsMuiPlayer from "@v2/components/CustomHlsMuiPlayer"

function parseStartSeconds(input: unknown): number | undefined {
    if (!input) return undefined;
    const raw = String(input).trim()
        .replace(/[۰-۹]/g, (c) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(c)))
        .replace(/[٠-٩]/g, (c) => String("٠١٢٣٤٥٦٧٨٩".indexOf(c)))
        .replace(/٫/g, ".");
    if (!raw) return undefined;
    if (/^\d+(\.\d+)?$/.test(raw)) {
        const value = Number(raw);
        return Number.isFinite(value) && value >= 0 ? value : undefined;
    }
    if (/^\d{1,2}(:\d{1,2}){1,2}(\.\d+)?$/.test(raw)) {
        const parts = raw.split(":").map(Number);
        if (parts.some((p) => !Number.isFinite(p) || p < 0)) return undefined;
        return parts.reduce((acc, part) => acc * 60 + part, 0);
    }
    return undefined;
}

type VideoPlayerMobileStickyProps = {
    video: VideoType
}

export default function VideoPlayerMobileSticky({ video }: VideoPlayerMobileStickyProps) {
    const menuIsOpen = useAppSelector(s => s.general.headerIsShow)
    // Read on the client rather than through useSearchParams: on a statically
    // rendered route that hook drops this whole subtree out of the prerendered HTML,
    // leaving crawlers with no <video> element at all. The start time is only needed
    // once playback begins, which is well after hydration.
    // Resolved once on mount. It never reaches the markup, only the player's playback
    // logic, so the server returning undefined is not a hydration mismatch.
    const [startAtSeconds] = useState<number | undefined>(() => {
        if (typeof window === "undefined") return undefined
        const params = new URLSearchParams(window.location.search)
        return (
            parseStartSeconds(params.get("t")) ??
            parseStartSeconds(params.get("start")) ??
            parseStartSeconds(params.get("s"))
        )
    })

    return (
        <Box sx={{
            position: { xs: "sticky", md: "static" },
            top: { xs: (menuIsOpen ? 92 : 36), md: "auto" },
            zIndex: { xs: 10, md: "auto" },
            bgcolor: "#000",
            transition: "0.5s cubic-bezier(0.22,0.61,0.36,1)",
        }} data-video-mobile-sticky="true">
            <CustomHlsMuiPlayer data={video} startAtSeconds={startAtSeconds ?? undefined} />
        </Box>
    )
}
